import { Badge3D } from '@/components/Badge3D';
import { apiFetch } from '@/lib/api';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { z } from 'zod';

const BadgePublicResponse = z.object({
  fullName: z.string(),
  profesion: z.string().nullable(),
  empresa: z.string().nullable(),
  qrToken: z.string(),
});

type BadgePublicData = z.infer<typeof BadgePublicResponse>;

const searchSchema = z.object({
  token: z.string().uuid().optional(),
});

export const Route = createFileRoute('/badge')({
  validateSearch: searchSchema,
  component: BadgePage,
});

// ── Token-based view ─────────────────────────────────────────────────────────

type FetchState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ok'; data: BadgePublicData };

function BadgeFromToken({ token }: { token: string }) {
  const [state, setState] = useState<FetchState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        // Ruta publica: apiFetch no envia Authorization sin sesion, desenvuelve el envelope
        // de Go y lee error.message en los errores.
        const data = await apiFetch(`/api/public/badge/${token}`, {
          schema: BadgePublicResponse,
        });
        if (!cancelled) setState({ status: 'ok', data });
      } catch (err) {
        if (!cancelled) {
          setState({
            status: 'error',
            message: err instanceof Error ? err.message : 'Error al cargar la credencial',
          });
        }
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (state.status === 'loading') {
    return (
      <div className="min-h-screen bg-[#0d0d1a] flex flex-col items-center justify-center gap-4">
        <div className="w-8 h-8 rounded-full border-2 border-[#009EAE] border-t-transparent animate-spin" />
        <p className="text-white/40 text-sm">Cargando credencial…</p>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="min-h-screen bg-[#0d0d1a] flex flex-col items-center justify-center gap-4 px-6">
        <div className="w-16 h-16 rounded-full bg-red-900/30 flex items-center justify-center">
          <span className="text-red-400 text-3xl">✕</span>
        </div>
        <h1 className="text-white text-xl font-semibold">Credencial no encontrada</h1>
        <p className="text-white/40 text-sm text-center max-w-xs">
          El enlace puede ser inválido o haber expirado.
        </p>
      </div>
    );
  }

  const { fullName, profesion, empresa, qrToken } = state.data;

  return (
    <div className="w-full h-screen bg-[#0d0d1a] relative overflow-hidden">
      <Badge3D
        name={fullName}
        profesion={profesion ?? undefined}
        empresa={empresa ?? undefined}
        qrToken={qrToken}
      />
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 pointer-events-none">
        <p className="text-white/40 text-sm select-none">Arrastra la credencial para interactuar</p>
      </div>
    </div>
  );
}

// ── Demo (no token) view ─────────────────────────────────────────────────────

function BadgeDemo() {
  const [name, setName] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = inputValue.trim();
    if (trimmed) {
      setName(trimmed);
      setSubmitted(true);
    }
  };

  if (submitted) {
    return (
      <div className="w-full h-screen bg-[#0d0d1a] relative overflow-hidden">
        <Badge3D
          name={name}
          profesion="Desarrollador"
          empresa="CIMO IPS"
          qrToken="00000000-0000-0000-0000-000000000001"
        />
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 pointer-events-none">
          <p className="text-white/40 text-sm select-none">
            Arrastra la credencial para interactuar
          </p>
          <button
            type="button"
            className="pointer-events-auto px-5 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white/60 hover:text-white/90 text-sm transition-all"
            onClick={() => setSubmitted(false)}
          >
            ← Cambiar nombre
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d1a] flex flex-col items-center justify-center gap-10 px-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#009EAE]/30 bg-[#009EAE]/10 mb-4">
          <span className="w-2 h-2 rounded-full bg-[#009EAE] animate-pulse" />
          <span className="text-[#009EAE] text-xs font-medium tracking-wide uppercase">
            Credencial Digital
          </span>
        </div>
        <h1 className="text-4xl font-bold text-white tracking-tight">CIMO IPS 2025</h1>
        <p className="text-white/50 text-base max-w-xs">
          Ingresa tu nombre para ver tu credencial interactiva del simposio
        </p>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="w-full max-w-sm flex flex-col gap-4">
        <input
          type="text"
          placeholder="Tu nombre completo"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          className="w-full px-5 py-4 rounded-xl bg-white/8 border border-white/15 text-white placeholder:text-white/30 focus:outline-none focus:border-[#009EAE]/60 focus:bg-white/10 text-center text-lg transition-all"
          maxLength={60}
        />
        <button
          type="submit"
          disabled={!inputValue.trim()}
          className="w-full py-4 rounded-xl bg-[#522B9A] hover:bg-[#6b3dbf] disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold text-base transition-all"
        >
          Ver mi credencial →
        </button>
      </form>

      {/* Hint */}
      <p className="text-white/20 text-xs text-center max-w-xs">
        La credencial es interactiva — la podrás arrastrar con el cursor
      </p>
    </div>
  );
}

// ── Route component ──────────────────────────────────────────────────────────

function BadgePage() {
  const { token } = Route.useSearch();

  if (token) {
    return <BadgeFromToken token={token} />;
  }

  return <BadgeDemo />;
}
